import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { BlockDownload } from '../block-download';
import { BlockView } from '../block-view';
import { Block, BlockChild, BlockColumn, BlockPage } from '../julia-block.model';
import { BlockSort, JuliaBlocksService } from '../julia-blocks.service';

/** A table Julia showed, or one list of a record (`child`): its rows page by page, sorted by the server on a header
 * click; a table is exported as `.xlsx` and its key column opens each row in the platform. */
@Component({
  selector: 'app-julia-table-block',
  templateUrl: './julia-table-block.component.html',
  styleUrls: ['./julia-table-block.component.scss'],
})
export class JuliaTableBlockComponent implements BlockView, OnInit {
  @Input() block!: Block;
  @Input() sessionId!: string;
  @Output() expired = new EventEmitter<void>();
  @Input() child: BlockChild | null = null;

  page: BlockPage | null = null;
  sort: BlockSort | null = null;
  loading = false;
  exporting = false;
  error: string | null = null;

  constructor(private blocks: JuliaBlocksService, private router: Router) {}

  ngOnInit() {
    if (this.block.page && !this.child) {
      this.page = this.block.page;
    } else {
      this.load(1);
    }
  }

  get columns(): BlockColumn[] {
    return this.child ? this.child.columns : this.block.columns;
  }

  get size(): number {
    return this.page?.size ?? this.block.page?.size ?? 25;
  }

  get first(): number {
    return this.page && this.page.total ? (this.page.page - 1) * this.page.size + 1 : 0;
  }

  get last(): number {
    return this.page ? Math.min(this.page.page * this.page.size, this.page.total) : 0;
  }

  get pages(): number {
    return this.page ? Math.max(1, Math.ceil(this.page.total / this.page.size)) : 1;
  }

  title(column: BlockColumn): string {
    return column.title || column.field;
  }

  cell(row: { [title: string]: any }, column: BlockColumn): string {
    const value = row[this.title(column)];
    return value === null || value === undefined ? '' : String(value);
  }

  /** The column whose cells open the row: the declared key, else the first. */
  isLinkColumn(index: number): boolean {
    if (this.child) return false;
    const keyIndex = this.columns.findIndex(c => c.field === this.block.key);
    return index === (keyIndex >= 0 ? keyIndex : 0);
  }

  link(rowIndex: number): string | null {
    return this.page?.links?.[rowIndex] ?? null;
  }

  open(event: MouseEvent, link: string) {
    event.preventDefault();
    this.router.navigateByUrl(link);
  }

  sortIcon(column: BlockColumn): string {
    if (this.sort?.[0] !== column.field) return '↕';
    return this.sort[1] === 'asc' ? '↑' : '↓';
  }

  /** asc, then desc, then the block's own order. */
  toggleSort(column: BlockColumn) {
    if (this.sort?.[0] !== column.field) {
      this.sort = [column.field, 'asc'];
    } else {
      this.sort = this.sort[1] === 'asc' ? [column.field, 'desc'] : null;
    }
    this.load(1);
  }

  go(page: number) {
    if (page < 1 || page > this.pages || this.loading) return;
    this.load(page);
  }

  load(page: number) {
    this.loading = true;
    this.blocks.rows(this.sessionId, this.block.id, page, this.size, this.sort, this.child?.name).subscribe({
      next: (result) => {
        this.page = result;
        this.error = null;
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.error = this.errorKey(err);
        this.loading = false;
      },
    });
  }

  download() {
    this.exporting = true;
    this.blocks.export(this.sessionId, this.block.id, this.sort).subscribe({
      next: (response) => {
        BlockDownload.save(response, BlockDownload.fileName(this.block.alias));
        this.exporting = false;
      },
      error: (err: HttpErrorResponse) => {
        this.error = this.errorKey(err);
        this.exporting = false;
      },
    });
  }

  private errorKey(err: HttpErrorResponse): string {
    if (err.status === 410) {
      this.expired.emit();
      return 'julia.blocks.expired';
    }
    if (err.status === 404) return 'julia.blocks.not_found';
    return 'julia.blocks.error';
  }
}
